<template>
  <div class="album-creation-form">
    <!-- Progress indicator -->
    <div class="form-progress">
      <div class="progress-steps">
        <div class="progress-step" :class="{ active: !authStore.isAuthValid, completed: authStore.isAuthValid }">
          <span class="step-number">1</span>
          <span class="step-label">Authentication</span>
        </div>
        <div class="progress-divider"></div>
        <div class="progress-step" :class="{
          active: authStore.isAuthValid && !showReviewStep,
          completed: authStore.isAuthValid && showReviewStep
        }">
          <span class="step-number">2</span>
          <span class="step-label">Album Details</span>
        </div>
        <div class="progress-divider" v-if="workflowMode === 'input' && spotifyData"></div>
        <div class="progress-step" v-if="workflowMode === 'input' && spotifyData" :class="{
          active: showReviewStep,
          completed: false
        }">
          <span class="step-number">3</span>
          <span class="step-label">Review & Confirm</span>
        </div>
      </div>
    </div>

    <!-- Authentication Check -->
    <div v-if="!authStore.isAuthValid" class="auth-form">
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">🔐 Admin Authentication</h2>
          <p class="section-description">Please authenticate to add new albums</p>
        </div>
      </div>

      <div class="form-actions">
        <button
          type="button"
          class="primary-button"
          @click="handleAuthRequired"
        >
          Authenticate
        </button>
      </div>
    </div>

    <!-- Album Details Step -->
    <form v-else @submit.prevent="submitAlbumForm" class="album-form">
      <!-- Basic Information Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">🎵 Basic Information</h2>
          <p class="section-description">Core album details from Spotify</p>
        </div>
        
        <div class="form-row highlighted-section">
          <EnhancedFormField
            v-model="form.spotify_id"
            label="Spotify Album ID"
            type="text"
            placeholder="e.g., 4aawyAB9vmqN3uQ7FjRGTy"
            :required="true"
            :error="getFieldError('spotify_id')"
            help-text="22-character Spotify album identifier"
            @validate="handleFieldValidation('spotify_id', $event)"
            @input="onSpotifyIdInput"
            :loading="spotifyLoading"
          />
          <div v-if="spotifyLoading" class="spotify-loading">
            <span class="loading-spinner"></span>
            <span>Looking up album on Spotify...</span>
          </div>
          <div v-if="spotifyError" class="spotify-error">
            ⚠️ {{ spotifyError }}
            <button 
              v-if="form.spotify_id && form.spotify_id.length === 22"
              type="button" 
              class="retry-button"
              @click="retrySpotifyLookup"
              :disabled="spotifyLoading"
            >
              Retry
            </button>
          </div>
          <div v-if="spotifySuccess" class="spotify-success">
            ✅ Album details auto-populated from Spotify
          </div>
        </div>
        
        <!-- Tidal ID Field -->
        <div class="form-row tidal-section">
          <TidalIdField
            v-model="form.tidal_album_id"
            :error="getFieldError('tidal_album_id')"
            @validate="handleFieldValidation('tidal_album_id', $event)"
          />
        </div>

        <!-- Genres -->
        <div class="form-row">
          <GenrePicker
            v-model="genreValue"
            label="Genres (auto-filled from Spotify, editable)"
          />
        </div>

        <!-- Spotify Image Preview Section -->
        <div v-if="spotifyData && (spotifyImagePreviewUrl || imageDownloadError)" class="form-row spotify-image-section">
          <div class="spotify-image-preview">
            <h4>🎨 Album Artwork from Spotify</h4>
            
            <!-- Image Preview -->
            <div v-if="spotifyImagePreviewUrl" class="spotify-artwork-preview">
              <img 
                :src="spotifyImagePreviewUrl" 
                :alt="`${spotifyData.name} - ${spotifyData.artists?.[0]?.name || 'Unknown Artist'} album cover`"
                class="preview-artwork"
              />
              <div class="artwork-info">
                <p class="artwork-title"><strong>{{ spotifyData.name }}</strong></p>
                <p class="artwork-artist">by {{ spotifyData.artists?.[0]?.name || 'Unknown Artist' }}</p>
                <p class="artwork-note">✨ Artwork automatically downloaded from Spotify</p>
              </div>
            </div>
            
            <!-- Image Download Loading -->
            <div v-if="imageDownloadLoading" class="image-loading">
              <span class="loading-spinner"></span>
              <span>Downloading album artwork...</span>
            </div>
            
            <!-- Image Download Error -->
            <div v-if="imageDownloadError" class="image-error">
              ⚠️ {{ imageDownloadError }}
              <button 
                v-if="canRetryImageDownload"
                type="button" 
                class="retry-image-button"
                @click="retryImageDownload"
                :disabled="imageDownloadLoading"
              >
                Retry Download
              </button>
            </div>
            
            <!-- Manual Upload Fallback -->
            <div v-if="showManualUploadFallback" class="manual-upload-fallback">
              <p class="fallback-message">You can upload the album cover manually instead:</p>
              <ImageUploader 
 
                @image-uploaded="onImageUploaded"
                :loading="uploadLoading"
              />
            </div>
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model="form.artist"
              label="Artist"
              type="text"
              placeholder="Enter artist name"
              :required="true"
              :error="getFieldError('artist')"
              @validate="handleFieldValidation('artist', $event)"
            />
          </div>
          <div class="form-col">
            <EnhancedFormField
              v-model="form.title"
              label="Album Title"
              type="text"
              placeholder="Enter album title"
              :required="true"
              :error="getFieldError('title')"
              @validate="handleFieldValidation('title', $event)"
            />
          </div>
        </div>
        
        <EnhancedFormField
          v-model="form.spotify_url"
          label="Spotify URL"
          type="url"
          placeholder="https://open.spotify.com/album/..."
          :required="true"
          :error="getFieldError('spotify_url')"
          help-text="Full Spotify album URL"
          @validate="handleFieldValidation('spotify_url', $event)"
        />

        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model.number="form.release_year"
              label="Release Year"
              type="number"
              placeholder="e.g. 1994"
              :min="1900"
              :max="new Date().getFullYear()"
              help-text="Auto-filled from Spotify"
            />
          </div>
          <div class="form-col">
            <EnhancedFormField
              v-model="form.release_month"
              label="Release Month"
              type="select"
              :options="monthOptions"
              help-text="Auto-filled from Spotify"
            />
          </div>
        </div>
      </div>

      <!-- Review Information Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">📊 Review Information</h2>
          <p class="section-description">Session and rating details</p>
        </div>
        
        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model="form.session_id"
              label="Session"
              type="select"
              placeholder="Select a session"
              :required="true"
              :options="sessionOptions"
              :error="getFieldError('session_id')"
              @validate="handleFieldValidation('session_id', $event)"
            />
          </div>
          <div class="form-col">
            <EnhancedFormField
              v-model="form.tier"
              label="Initial Tier"
              type="select"
              placeholder="Select initial tier"
              :required="true"
              :options="tierOptions"
              :error="getFieldError('tier')"
              help-text="Can be changed later during review"
              @validate="handleFieldValidation('tier', $event)"
            />
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model="form.submitted_by"
              label="Submitted By"
              type="select"
              placeholder="Select submitter"
              :required="true"
              :options="submitterOptions"
              :error="getFieldError('submitted_by')"
              @validate="handleFieldValidation('submitted_by', $event)"
            />
          </div>
        </div>
      </div>

      <!-- Special Options Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">⚙️ Special Options</h2>
          <p class="section-description">Additional album settings</p>
        </div>
        
        <!-- Test Album Toggle -->
        <div class="test-album-section" :data-dev="isDevelopment">
          <EnhancedFormField
            v-model="form.is_test_album"
            label=""
            type="checkbox"
            checkbox-label="Test Album"
            :error="getFieldError('is_test_album')"
            help-text="Create as test data for development purposes"
            @validate="handleFieldValidation('is_test_album', $event)"
          />
        </div>
        
        <EnhancedFormField
          v-model="form.veto"
          label=""
          type="checkbox"
          checkbox-label="This album has been vetoed"
          :error="getFieldError('veto')"
          help-text="Check if this album was rejected during review"
          @validate="handleFieldValidation('veto', $event)"
        />
        
        <div v-if="form.veto" class="veto-details">
          <EnhancedFormField
            v-model="form.veto_by"
            label="Vetoed By"
            type="text"
            placeholder="Name of person who vetoed"
            :error="getFieldError('veto_by')"
            @validate="handleFieldValidation('veto_by', $event)"
          />
        </div>
      </div>

      <!-- Image Upload Section -->
      <div class="form-section" v-if="!spotifyImagePreviewUrl || showManualUploadSection">
        <div class="section-header">
          <h2 class="section-title">🖼️ Album Cover</h2>
          <p class="section-description" v-if="!spotifyImagePreviewUrl">Upload the album cover image</p>
          <p class="section-description" v-else>Manual image upload (replaces Spotify artwork)</p>
        </div>
        
        <div class="image-upload-section">
          <ImageUploader 
            :password="form.password" 
            @image-uploaded="onImageUploaded"
            :loading="uploadLoading"
          />
          <div v-if="!hasValidImage" class="upload-requirement">
            ⚠️ Album cover is required before saving
          </div>
          <div v-if="spotifyImagePreviewUrl && showManualUploadSection" class="manual-override-note">
            💡 Uploading a manual image will replace the Spotify artwork
          </div>
        </div>
        
        <div v-if="spotifyImagePreviewUrl && !showManualUploadSection" class="show-manual-upload">
          <button 
            type="button" 
            class="link-button"
            @click="showManualUploadSection = true"
          >
            📤 Upload different image instead
          </button>
        </div>
      </div>

      <!-- Review Step -->
      <div v-if="showReviewStep" class="review-step">
        <div class="form-section">
          <div class="section-header">
            <h2 class="section-title">📋 Review Album Information</h2>
            <p class="section-description">Review and edit the auto-populated information before saving</p>
          </div>
          
          <!-- Review Content -->
          <div class="review-content">
            <!-- Album Title Review -->
            <div class="review-field">
              <label class="review-label">Album Title</label>
              <div class="field-comparison">
                <div class="auto-populated-value">
                  <span class="label">Auto-populated:</span>
                  <span class="value">{{ autoPopulatedData.title }}</span>
                </div>
                <EnhancedFormField
                  v-model="form.title"
                  label=""
                  type="text"
                  placeholder="Enter album title"
                  :required="true"
                  :error="getFieldError('title')"
                  :class="{ 'field-modified': fieldModifications.title }"
                  @input="markFieldModified('title')"
                  @validate="handleFieldValidation('title', $event)"
                />
                <div v-if="fieldModifications.title" class="modification-indicator">
                  ✏️ Modified by user
                </div>
              </div>
            </div>
            
            <!-- Artist Review -->
            <div class="review-field">
              <label class="review-label">Artist</label>
              <div class="field-comparison">
                <div class="auto-populated-value">
                  <span class="label">Auto-populated:</span>
                  <span class="value">{{ autoPopulatedData.artist }}</span>
                </div>
                <EnhancedFormField
                  v-model="form.artist"
                  label=""
                  type="text"
                  placeholder="Enter artist name"
                  :required="true"
                  :error="getFieldError('artist')"
                  :class="{ 'field-modified': fieldModifications.artist }"
                  @input="markFieldModified('artist')"
                  @validate="handleFieldValidation('artist', $event)"
                />
                <div v-if="fieldModifications.artist" class="modification-indicator">
                  ✏️ Modified by user
                </div>
              </div>
            </div>
            
            <!-- Image Review -->
            <div class="review-field">
              <label class="review-label">Album Artwork</label>
              <div class="image-review">
                <div class="current-image">
                  <img 
                    v-if="uploadedImageUrl || spotifyImagePreviewUrl" 
                    :src="uploadedImageUrl || spotifyImagePreviewUrl" 
                    :alt="`${form.artist} - ${form.title} album cover`"
                    class="review-artwork"
                  />
                  <div class="image-source">
                    <span class="source-label">
                      {{ uploadedImageUrl ? '📤 Manually uploaded' : '🎵 From Spotify' }}
                    </span>
                    <div v-if="fieldModifications.image" class="modification-indicator">
                      ✏️ Modified by user
                    </div>
                  </div>
                </div>
                <div class="image-actions">
                  <button 
                    type="button" 
                    class="secondary-button"
                    @click="toggleManualImageUpload"
                  >
                    {{ uploadedImageUrl ? 'Change Image' : 'Upload Different Image' }}
                  </button>
                  <button 
                    v-if="uploadedImageUrl && autoPopulatedData.imageUrl"
                    type="button" 
                    class="link-button"
                    @click="restoreSpotifyImage"
                  >
                    Restore Spotify Image
                  </button>
                </div>
              </div>
              
              <!-- Manual Upload Section for Review -->
              <div v-if="showManualUploadSection" class="manual-upload-in-review">
                <ImageUploader 
   
                  @image-uploaded="onImageUploadedInReview"
                  :loading="uploadLoading"
                />
              </div>
            </div>
          </div>
          
          <!-- Review Actions -->
          <div class="review-actions">
            <button 
              type="button" 
              class="secondary-button"
              @click="returnToInput"
            >
              ← Back to Input
            </button>
            <button 
              type="button" 
              class="link-button"
              @click="switchToManualMode"
            >
              Switch to Manual Entry
            </button>
            <button 
              type="button" 
              class="primary-button"
              :disabled="!canSubmit"
              :class="{ loading: submitLoading }"
              @click="proceedToSave"
            >
              <span v-if="submitLoading" class="button-spinner"></span>
              {{ submitLoading ? 'Creating Album...' : 'Confirm & Save Album' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Hidden Admin Password (for API calls) -->

      <!-- Form Actions -->
      <div class="form-actions" v-if="!showReviewStep">
        <button 
          type="button" 
          class="secondary-button"
          @click="goBackToAuth"
        >
          ← Back to Authentication
        </button>
        
        <!-- Auto-population flow: Proceed to Review -->
        <button 
          v-if="workflowMode === 'input' && spotifyData && hasValidImage"
          type="button" 
          class="primary-button"
          :disabled="!canProceedToReview"
          @click="proceedToReview"
        >
          Proceed to Review →
        </button>
        
        <!-- Manual flow or no auto-population: Direct Save -->
        <button 
          v-else
          type="submit" 
          class="primary-button"
          :disabled="!canSubmit"
          :class="{ loading: submitLoading }"
        >
          <span v-if="submitLoading" class="button-spinner"></span>
          {{ submitLoading ? 'Creating Album...' : 'Save Album' }}
        </button>
        
        <!-- Manual Mode Toggle -->
        <button 
          v-if="workflowMode === 'input' && spotifyData"
          type="button" 
          class="link-button"
          @click="switchToManualMode"
          style="margin-left: 1rem;"
        >
          Switch to Manual Entry
        </button>
      </div>
      
      <!-- Status Messages -->
      <div v-if="success" class="status-message success-message" role="status">
        ✅ {{ success }}
      </div>
      <div v-if="error" class="status-message error-message" role="alert">
        ⚠️ {{ error }}
      </div>
    </form>

    <!-- Admin Authentication Modal -->
    <AdminAuthModal
      :visible="showAuthModal"
      @authenticated="onAuthenticated"
      @close="showAuthModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import EnhancedFormField from './EnhancedFormField.vue'
import TidalIdField from './TidalIdField.vue'
import ImageUploader from '../ImageUploader.vue'
import AdminAuthModal from '../modals/AdminAuthModal.vue'
import GenrePicker from './GenrePicker.vue'
import { useFormValidation, albumValidationSchema } from '../../composables/useFormValidation.js'
import { useSpotifyService } from '../../composables/useSpotifyService.js'
import { useSpotifyImageService } from '../../composables/useSpotifyImageService.js'
import { useTestMode } from '../../composables/useTestMode.js'
import { useAuthStore } from '../../stores/auth.js'
import apiService from '../../apiService.js'

export default {
  name: 'AlbumCreationForm',
  components: {
    EnhancedFormField,
    TidalIdField,
    ImageUploader,
    AdminAuthModal,
    GenrePicker
  },
  setup() {
    const authStore = useAuthStore()
    const router = useRouter()
    
    // Test mode functionality
    const { getTestAlbumCreationDefault } = useTestMode()
    
    // Form validation
    const {
      validateForm,
      hasError,
      getFieldError,
      isFormValid
    } = useFormValidation()
    
    // Spotify service
    const { loading: spotifyLoading, error: spotifyError, lookupAlbum } = useSpotifyService()
    
    // Spotify image service
    const { 
      imageLoading: imageDownloadLoading,
      imageError: imageDownloadError,
      downloadedImage: spotifyDownloadedImage,
      imagePreviewUrl: spotifyImagePreviewUrl,
      downloadAlbumImage,
      clearImageCache,
      validateImageData
    } = useSpotifyImageService()
    
    // Form state
    const form = ref({
      spotify_id: '',
      tidal_album_id: '',
      artist: '',
      title: '',
      submitted_by: '',
      session_id: '',
      tier: 'Not rated yet',
      spotify_url: '',
      veto: false,
      veto_by: '',
      is_test_album: getTestAlbumCreationDefault(),
      release_year: null,
      release_month: null,
      mainGenres: [],
      subGenres: [],
    })

    // UI state
    const showAuthModal = ref(false)
    const uploadedImageUrl = ref(null)
    const sessions = ref([])
    const submitLoading = ref(false)
    const uploadLoading = ref(false)
    const success = ref('')
    const error = ref('')
    const spotifySuccess = ref('')
    const spotifyData = ref(null)
    const showManualUploadSection = ref(false)
    const showManualUploadFallback = ref(false)
    
    // Review workflow state
    const workflowMode = ref('input') // 'input', 'review', 'manual'
    const showReviewStep = ref(false)
    const autoPopulatedData = ref({
      title: '',
      artist: '',
      imageUrl: ''
    })
    const fieldModifications = ref({
      title: false,
      artist: false,
      image: false
    })
    
    // Options for select fields
    const submitterOptions = computed(() => [
      { value: 'Arno', label: 'Arno' },
      { value: 'Tyrone', label: 'Tyrone' },
      { value: 'Lucas', label: 'Lucas' },
      { value: 'Thomas', label: 'Thomas' },
      { value: 'Wouter', label: 'Wouter' }
    ])
    
    const monthOptions = [
      { value: null, label: 'Unknown' },
      { value: 1, label: 'January' }, { value: 2, label: 'February' },
      { value: 3, label: 'March' }, { value: 4, label: 'April' },
      { value: 5, label: 'May' }, { value: 6, label: 'June' },
      { value: 7, label: 'July' }, { value: 8, label: 'August' },
      { value: 9, label: 'September' }, { value: 10, label: 'October' },
      { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ]

    const tierOptions = computed(() => [
      { value: 'Now Listening To', label: '🎵 Now Listening To' },
      { value: 'Not rated yet', label: '❓ Not rated yet' },
      { value: 'Unknown', label: '❓ Unknown' },
      { value: 'S', label: '🏆 S Tier' },
      { value: 'A', label: '🥇 A Tier' },
      { value: 'B', label: '🥈 B Tier' },
      { value: 'C', label: '🥉 C Tier' },
      { value: 'D', label: '📀 D Tier' },
      { value: 'E', label: '💿 E Tier' },
      { value: 'F', label: '🔻 F Tier' },
      { value: 'Tiering Herrie', label: '🗑️ Tiering Herrie' }
    ])
    
    const sessionOptions = computed(() => 
      sessions.value.map(session => ({
        value: session.id,
        label: session.label
      }))
    )
    
    // Image-related computed properties
    const hasValidImage = computed(() => {
      return !!(uploadedImageUrl.value || spotifyImagePreviewUrl.value)
    })
    
    const canRetryImageDownload = computed(() => {
      return spotifyData.value && 
             imageDownloadError.value && 
             !spotifyImagePreviewUrl.value
    })
    
    // Form validation
    const canSubmit = computed(() => {
      return isFormValid.value &&
             hasValidImage.value &&
             !submitLoading.value &&
             authStore.isAuthValid
    })
    
    const canProceedToReview = computed(() => {
      return form.value.session_id && 
             form.value.submitted_by && 
             form.value.tier &&
             hasValidImage.value &&
             (form.value.title || autoPopulatedData.value.title) &&
             (form.value.artist || autoPopulatedData.value.artist)
    })
    
    // Spotify lookup with debouncing
    let spotifyLookupTimeout = null
    
    const performSpotifyLookup = async (spotifyId) => {
      try {
        const spotifyResponse = await lookupAlbum(spotifyId)
        
        // Auto-populate form fields
        if (spotifyResponse && spotifyResponse.data) {
          const albumData = spotifyResponse.data
          
          // Store Spotify data for image processing
          spotifyData.value = albumData
          
          // Extract artist name from artists array
          const artistName = albumData.artists && albumData.artists.length > 0 
            ? albumData.artists[0].name 
            : ''
          
          // Store auto-populated data for review comparison
          autoPopulatedData.value = {
            title: albumData.name || '',
            artist: artistName,
            imageUrl: '', // Will be set when image is downloaded
            tidalId: '' // Will be set if Tidal ID is found
          }
          
          // Populate form fields with correct mapping
          form.value.artist = artistName
          form.value.title = albumData.name || ''
          form.value.spotify_url = albumData.external_urls?.spotify || `https://open.spotify.com/album/${spotifyId}`
          form.value.release_year = albumData.release_date
            ? parseInt(albumData.release_date.substring(0, 4), 10)
            : null
          form.value.release_month = albumData.release_date?.length >= 7
            ? parseInt(albumData.release_date.substring(5, 7), 10)
            : null
          const suggested = albumData.suggestedGenres || {};
          form.value.mainGenres = suggested.mainGenres || [];
          form.value.subGenres  = suggested.subGenres  || [];

          // Set workflow mode to input (with auto-population)
          workflowMode.value = 'input'
          
          spotifySuccess.value = 'Album details auto-populated from Spotify'
          
          console.log('Auto-populated form with:', {
            artist: form.value.artist,
            title: form.value.title,
            spotify_url: form.value.spotify_url
          })
          
          // Automatically download album image
          await downloadSpotifyImage(albumData)
        }
      } catch (err) {
        console.error('Spotify lookup failed:', err)
        // Error is handled by the Spotify service and displayed in spotifyError
        // Clear any previous image data on lookup failure
        clearImageCache()
        spotifyData.value = null
        showManualUploadFallback.value = true
      }
    }
    
    const downloadSpotifyImage = async (albumData) => {
      try {
        console.log('Starting automatic image download for:', albumData.name)
        
        const imageData = await downloadAlbumImage(albumData)
        
        if (imageData) {
          console.log('Successfully downloaded and cached Spotify image')
          
          // Store auto-populated image URL for review comparison
          autoPopulatedData.value.imageUrl = spotifyImagePreviewUrl.value || imageData.url
          
          // Validate the downloaded image
          const validation = validateImageData(imageData)
          if (!validation.isValid) {
            console.warn('Downloaded image validation failed:', validation.errors)
            imageDownloadError.value = validation.errors.join(', ')
            showManualUploadFallback.value = true
            return
          }
          
          // Log any warnings
          if (validation.warnings.length > 0) {
            console.info('Image validation warnings:', validation.warnings)
          }
          
          // Reset manual upload fallback
          showManualUploadFallback.value = false
          showManualUploadSection.value = false
          
          // Debug: Log the current state
          console.log('Image download completed - Debug state:', {
            spotifyImagePreviewUrl: spotifyImagePreviewUrl.value,
            uploadedImageUrl: uploadedImageUrl.value,
            hasValidImage: hasValidImage.value,
            spotifyDownloadedImage: !!spotifyDownloadedImage.value
          })
        }
      } catch (err) {
        console.error('Failed to download Spotify image:', err)
        // Error is already set by the service
        showManualUploadFallback.value = true
      }
    }
    
    const onSpotifyIdInput = () => {
      const spotifyId = form.value.spotify_id
      
      // Clear previous timeout
      if (spotifyLookupTimeout) {
        clearTimeout(spotifyLookupTimeout)
      }
      
      // Clear previous messages
      spotifySuccess.value = ''
      
      // Check if Spotify ID is valid format (22 characters)
      if (spotifyId && spotifyId.length === 22) {
        spotifyLookupTimeout = setTimeout(() => performSpotifyLookup(spotifyId), 800) // 800ms debounce
      }
    }
    
    const retrySpotifyLookup = async () => {
      const spotifyId = form.value.spotify_id
      if (spotifyId && spotifyId.length === 22) {
        await performSpotifyLookup(spotifyId)
      }
    }
    
    const retryImageDownload = async () => {
      if (spotifyData.value) {
        await downloadSpotifyImage(spotifyData.value)
      }
    }
    
    
    
    // Methods
    const handleFieldValidation = () => {
      // The validation is already handled by the useFormValidation composable
      // This is just for any additional logic if needed
    }

    const handleAuthRequired = () => {
      if (!authStore.isAuthValid) {
        showAuthModal.value = true
      }
    }

    const onAuthenticated = () => {
      showAuthModal.value = false
      // Auth state now valid, form will become accessible
    }
    
    const onImageUploaded = (url) => {
      uploadedImageUrl.value = url
      uploadLoading.value = false
    }
    
    // Review workflow methods
    const proceedToReview = () => {
      showReviewStep.value = true
      workflowMode.value = 'review'
      
      // Reset field modifications
      fieldModifications.value = {
        title: false,
        artist: false,
        image: false
      }
    }
    
    const returnToInput = () => {
      showReviewStep.value = false
      workflowMode.value = 'input'
      
      // Reset modifications but keep the form data
      fieldModifications.value = {
        title: false,
        artist: false,
        image: false
      }
    }
    
    const markFieldModified = (fieldName) => {
      const originalValue = autoPopulatedData.value[fieldName === 'title' ? 'title' : fieldName === 'artist' ? 'artist' : '']
      const currentValue = fieldName === 'title' ? form.value.title : 
                          fieldName === 'artist' ? form.value.artist : ''
      
      fieldModifications.value[fieldName] = originalValue !== currentValue
    }
    
    const switchToManualMode = () => {
      workflowMode.value = 'manual'
      showReviewStep.value = false
      
      // Clear auto-populated data
      spotifyData.value = null
      spotifySuccess.value = ''
      clearImageCache()
      uploadedImageUrl.value = null
      
      // Reset form to manual entry state
      form.value.spotify_id = ''
      form.value.artist = ''
      form.value.title = ''
      form.value.spotify_url = ''
      
      // Reset modification tracking
      autoPopulatedData.value = {
        title: '',
        artist: '',
        imageUrl: ''
      }
      fieldModifications.value = {
        title: false,
        artist: false,
        image: false
      }
    }
    
    const proceedToSave = async () => {
      await submitAlbumForm()
    }
    
    const onImageUploadedInReview = (url) => {
      uploadedImageUrl.value = url
      uploadLoading.value = false
      fieldModifications.value.image = true
      showManualUploadSection.value = false
    }
    
    const toggleManualImageUpload = () => {
      showManualUploadSection.value = !showManualUploadSection.value
    }
    
    const restoreSpotifyImage = () => {
      uploadedImageUrl.value = null
      fieldModifications.value.image = false
      showManualUploadSection.value = false
    }
    
    const submitAlbumForm = async () => {
      // Create a modified validation schema that excludes cover_url if we have a Spotify image
      const validationSchema = { ...albumValidationSchema }

      // Password is now handled by AdminAuthModal (Story 1.4), so we don't validate it in the form
      delete validationSchema.password

      // If we have a Spotify image, we don't need to validate cover_url
      if (spotifyImagePreviewUrl.value && spotifyDownloadedImage.value) {
        delete validationSchema.cover_url
      }
      
      // Validate entire form
      const isValid = validateForm(form.value, validationSchema)
      
      // Debug: Check validation errors
      const validationErrors = {}
      Object.keys(validationSchema).forEach(fieldName => {
        const field = validationSchema[fieldName]
        const value = form.value[fieldName]
        for (const rule of field.rules) {
          const error = rule(value)
          if (error) {
            validationErrors[fieldName] = error
            break
          }
        }
      })
      
      // Debug: Log current state at submission
      console.log('Form submission - Debug state:', {
        isValid,
        hasValidImage: hasValidImage.value,
        uploadedImageUrl: uploadedImageUrl.value,
        spotifyImagePreviewUrl: spotifyImagePreviewUrl.value,
        spotifyDownloadedImage: !!spotifyDownloadedImage.value,
        isFormValid: isFormValid.value,
        formData: form.value,
        validationErrors
      })
      
      if (!isValid || !hasValidImage.value) {
        error.value = 'Please fix the errors above and ensure album cover is available'
        return
      }
      
      submitLoading.value = true
      success.value = ''
      error.value = ''
      
      try {
        let finalImageUrl = uploadedImageUrl.value
        
        // If we have a Spotify image but no manual upload, process the Spotify image
        if (!uploadedImageUrl.value && spotifyDownloadedImage.value) {
          console.log('Processing and uploading Spotify image to S3...')
          
          try {
            // Process and upload the Spotify image
            const spotifyUploadResult = await processAndUploadSpotifyImage()
            if (spotifyUploadResult && spotifyUploadResult.success) {
              finalImageUrl = spotifyUploadResult.imageUrl
              console.log('Successfully uploaded Spotify image to S3:', finalImageUrl)
            } else {
              throw new Error(spotifyUploadResult?.error || 'Failed to upload Spotify image to S3')
            }
          } catch (uploadError) {
            console.error('Spotify image upload failed:', uploadError)
            error.value = `Failed to process album artwork: ${uploadError.message}. Please try uploading manually.`
            showManualUploadFallback.value = true
            return
          }
        }
        
        const albumData = {
          ...form.value,
          cover_url:  finalImageUrl,
          password:   authStore.adminPassword,
          mainGenres: form.value.mainGenres,
          subGenres:  form.value.subGenres,
        }

        await apiService.createAlbum(albumData)
        success.value = 'Album created successfully!'
        
        // Redirect after short delay
        setTimeout(() => {
          router.push('/')
        }, 2000)
        
      } catch (err) {
        error.value = err.message || 'Failed to create album. Please try again.'
      } finally {
        submitLoading.value = false
      }
    }
    
    const processAndUploadSpotifyImage = async () => {
      if (!spotifyDownloadedImage.value || !spotifyData.value) {
        throw new Error('No Spotify image data available')
      }
      
      // Create FormData for the Spotify image
      const formData = new FormData()
      
      // Convert blob to File object
      const imageFile = new File(
        [spotifyDownloadedImage.value.blob], 
        `spotify-${spotifyData.value.name || 'album'}.jpg`, 
        { type: spotifyDownloadedImage.value.blob.type }
      )
      
      formData.append('image', imageFile)
      formData.append('password', form.value.password)
      formData.append('source', 'spotify-auto-population')
      formData.append('album_name', spotifyData.value.name || '')
      formData.append('artist_name', spotifyData.value.artists?.[0]?.name || '')
      formData.append('spotify_id', form.value.spotify_id)
      
      try {
        console.log('Uploading Spotify image to S3 via API...', {
          fileName: imageFile.name,
          size: `${Math.round(imageFile.size / 1024)}KB`,
          type: imageFile.type
        })
        
        const response = await apiService.uploadAlbumImage(formData)
        
        if (response && response.success && response.imageUrl) {
          return {
            success: true,
            imageUrl: response.imageUrl,
            metadata: {
              source: 'spotify-auto-population',
              originalUrl: spotifyDownloadedImage.value.url,
              albumName: spotifyData.value.name,
              artist: spotifyData.value.artists?.[0]?.name
            }
          }
        } else {
          throw new Error('Upload API did not return a valid image URL')
        }
      } catch (err) {
        console.error('Failed to upload Spotify image via API:', err)
        throw new Error(`Image upload failed: ${err.message}`)
      }
    }
    
    // Load sessions on mount
    onMounted(async () => {
      try {
        const sessionsResponse = await apiService.WorkspaceSessions()
        sessions.value = (Array.isArray(sessionsResponse.data) ? sessionsResponse.data : sessionsResponse).map(s => ({
          ...s,
          label: s.session_date ? `${new Date(s.session_date).toLocaleDateString()} - ${s.location}` : s.location || s.id
        }))
      } catch (err) {
        console.error('Failed to load sessions:', err)
        sessions.value = []
      }
    })
    
    // Cleanup on unmount
    onUnmounted(() => {
      // Clear timeout
      if (spotifyLookupTimeout) {
        clearTimeout(spotifyLookupTimeout)
      }
      
      // Clear image cache and object URLs
      clearImageCache()
    })
    
    // Environment detection for template 
    const isDevelopment = computed(() => process.env.NODE_ENV === 'development')
    
    const genreValue = computed({
      get: () => ({ main: form.value.mainGenres, sub: form.value.subGenres }),
      set: (v) => { form.value.mainGenres = v.main; form.value.subGenres = v.sub; },
    });

    return {
      // Form data
      form,
      genreValue,
      uploadedImageUrl,

      // Auth state
      authStore,
      showAuthModal,

      // UI state
      submitLoading,
      uploadLoading,
      success,
      error,

      // Spotify state
      spotifyLoading,
      spotifyError,
      spotifySuccess,
      spotifyData,

      // Image state
      imageDownloadLoading,
      imageDownloadError,
      spotifyImagePreviewUrl,
      spotifyDownloadedImage,
      showManualUploadSection,
      showManualUploadFallback,

      // Review workflow state
      workflowMode,
      showReviewStep,
      autoPopulatedData,
      fieldModifications,

      // Options
      submitterOptions,
      monthOptions,
      tierOptions,
      sessionOptions,

      // Computed
      canSubmit,
      canProceedToReview,
      hasValidImage,
      canRetryImageDownload,

      // Form methods
      handleFieldValidation,
      handleAuthRequired,
      onAuthenticated,
      onImageUploaded,
      submitAlbumForm,

      // Review workflow methods
      proceedToReview,
      returnToInput,
      markFieldModified,
      switchToManualMode,
      proceedToSave,
      onImageUploadedInReview,
      toggleManualImageUpload,
      restoreSpotifyImage,

      // Spotify methods
      onSpotifyIdInput,
      retrySpotifyLookup,
      retryImageDownload,

      // Validation methods
      hasError,
      getFieldError,

      // Environment detection
      isDevelopment
    }
  }
}
</script>

<style scoped>
/* Import the tier colors */
@import '../../styles/tier-colors.css';

.album-creation-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  color: #f3f3f3;
}

/* Progress indicator */
.form-progress {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #404040;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.5;
  transition: var(--tier-transition-smooth);
}

.progress-step.active,
.progress-step.completed {
  opacity: 1;
}

.step-number {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #404040;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #f3f3f3;
  transition: var(--tier-transition-smooth);
}

.progress-step.active .step-number {
  background: var(--tier-d-color);
  color: white;
}

.progress-step.completed .step-number {
  background: var(--tier-c-color);
  color: white;
}

.step-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.progress-divider {
  width: 3rem;
  height: 2px;
  background: #404040;
  transition: var(--tier-transition-smooth);
}

.progress-step.completed + .progress-divider {
  background: var(--tier-c-color);
}

/* Form sections */
.form-section {
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* Test Album Toggle Section */
.test-album-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
  margin-top: 1rem;
}

.test-album-section[data-dev="true"] {
  background-color: rgba(0, 188, 212, 0.05);
  border: 1px solid rgba(0, 188, 212, 0.2);
  border-radius: 8px;
  padding: 1rem;
  position: relative;
}

.test-album-section[data-dev="true"]::before {
  content: 'DEV MODE';
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 0.7rem;
  color: rgba(0, 188, 212, 0.8);
  background: rgba(0, 188, 212, 0.1);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  border: 1px solid rgba(0, 188, 212, 0.3);
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #f3f3f3;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-description {
  font-size: 0.875rem;
  color: #aaa;
  margin: 0;
}

/* Form layout */
.form-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.form-col {
  flex: 1;
}

.highlighted-section {
  background: rgba(0, 191, 255, 0.05);
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 8px;
  padding: 1rem;
}

/* Special sections */
.veto-details {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 69, 0, 0.05);
  border: 1px solid rgba(255, 69, 0, 0.2);
  border-radius: 8px;
}

/* Spotify feedback */
.spotify-loading,
.spotify-error,
.spotify-success {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

/* Tidal section */
.tidal-section {
  background: rgba(0, 191, 255, 0.02);
  border: 1px solid rgba(0, 191, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}


.spotify-loading {
  background: rgba(0, 191, 255, 0.05);
  border: 1px solid rgba(0, 191, 255, 0.2);
  color: var(--tier-d-color);
}

.spotify-error {
  background: rgba(255, 69, 0, 0.05);
  border: 1px solid rgba(255, 69, 0, 0.2);
  color: var(--tier-s-color);
}

.spotify-success {
  background: rgba(50, 205, 50, 0.05);
  border: 1px solid rgba(50, 205, 50, 0.2);
  color: var(--tier-c-color);
}


.loading-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.retry-button {
  margin-left: auto;
  padding: 0.375rem 0.75rem;
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 4px;
  color: inherit;
  font-size: 0.75rem;
  cursor: pointer;
  transition: var(--tier-transition-fast);
}

.retry-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

.retry-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.image-upload-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.upload-requirement {
  color: var(--tier-s-color);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Form actions */
.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #404040;
}

/* Buttons */
.primary-button,
.secondary-button {
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--tier-transition-fast);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  border: none;
}

.primary-button {
  background: var(--tier-d-color);
  color: white;
  flex: 1;
  justify-content: center;
}

.primary-button:hover:not(:disabled) {
  background: var(--tier-d-light);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 191, 255, 0.3);
}

.primary-button:disabled {
  background: #666;
  color: #999;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.secondary-button {
  background: transparent;
  color: #f3f3f3;
  border: 1px solid #666;
}

.secondary-button:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: #888;
}

.button-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Status messages */
.status-message {
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.success-message {
  background: rgba(50, 205, 50, 0.1);
  border: 1px solid rgba(50, 205, 50, 0.3);
  color: var(--tier-c-color);
}

.error-message {
  background: rgba(255, 69, 0, 0.1);
  border: 1px solid rgba(255, 69, 0, 0.3);
  color: var(--tier-s-color);
}

/* Responsive design */
@media (max-width: 768px) {
  .album-creation-form {
    padding: 1rem;
    margin: 1rem;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .primary-button,
  .secondary-button {
    width: 100%;
    justify-content: center;
  }
  
  .progress-steps {
    gap: 0.5rem;
  }
  
  .progress-divider {
    width: 2rem;
  }
  
  .step-label {
    font-size: 0.75rem;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .form-section {
    border-width: 2px;
  }
  
  .primary-button,
  .secondary-button {
    border-width: 2px;
  }
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Spotify image preview section */
.spotify-image-section {
  background: rgba(30, 215, 96, 0.05);
  border: 1px solid rgba(30, 215, 96, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.spotify-image-preview h4 {
  color: #1ed760;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spotify-artwork-preview {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.preview-artwork {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.artwork-info {
  flex: 1;
}

.artwork-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #f3f3f3;
}

.artwork-artist {
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
  color: #ccc;
}

.artwork-note {
  font-size: 0.875rem;
  margin: 0;
  color: #1ed760;
  font-style: italic;
}

.image-loading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(30, 215, 96, 0.05);
  border: 1px solid rgba(30, 215, 96, 0.2);
  border-radius: 6px;
  color: #1ed760;
  font-size: 0.875rem;
}

.image-error {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 69, 0, 0.05);
  border: 1px solid rgba(255, 69, 0, 0.2);
  border-radius: 6px;
  color: var(--tier-s-color);
  font-size: 0.875rem;
}

.retry-image-button {
  margin-left: auto;
  padding: 0.375rem 0.75rem;
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 4px;
  color: inherit;
  font-size: 0.75rem;
  cursor: pointer;
  transition: var(--tier-transition-fast);
}

.retry-image-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

.retry-image-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.manual-upload-fallback {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.fallback-message {
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  color: #aaa;
}

.show-manual-upload {
  text-align: center;
  margin-top: 1rem;
}

.link-button {
  background: none;
  border: none;
  color: var(--tier-d-color);
  font-size: 0.875rem;
  text-decoration: underline;
  cursor: pointer;
  transition: var(--tier-transition-fast);
}

.link-button:hover {
  color: var(--tier-d-light);
}

.manual-override-note {
  color: #aaa;
  font-size: 0.875rem;
  font-style: italic;
  margin-top: 0.5rem;
}

/* Responsive design for image preview */
@media (max-width: 768px) {
  .spotify-artwork-preview {
    flex-direction: column;
    text-align: center;
  }
  
  .preview-artwork {
    width: 100px;
    height: 100px;
  }
  
  .image-loading,
  .image-error {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .retry-image-button {
    margin-left: 0;
    margin-top: 0.5rem;
  }
}

/* Review Step Styles */
.review-step {
  margin-top: 2rem;
}

.review-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.review-field {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1.5rem;
}

.review-label {
  display: block;
  font-size: 1.1rem;
  font-weight: 600;
  color: #f3f3f3;
  margin-bottom: 1rem;
}

.field-comparison {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.auto-populated-value {
  background: rgba(30, 215, 96, 0.05);
  border: 1px solid rgba(30, 215, 96, 0.2);
  border-radius: 6px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.auto-populated-value .label {
  font-size: 0.875rem;
  color: #1ed760;
  font-weight: 500;
  min-width: 120px;
}

.auto-populated-value .value {
  font-size: 1rem;
  color: #f3f3f3;
  font-weight: 500;
}

.modification-indicator {
  font-size: 0.875rem;
  color: var(--tier-d-color);
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.field-modified {
  border-color: var(--tier-d-color) !important;
  box-shadow: 0 0 0 1px rgba(0, 191, 255, 0.2);
}

.image-review {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.current-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.review-artwork {
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.image-source {
  text-align: center;
}

.source-label {
  font-size: 0.875rem;
  color: #aaa;
  display: block;
  margin-bottom: 0.5rem;
}

.image-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.manual-upload-in-review {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.review-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #404040;
  flex-wrap: wrap;
}

.review-actions .primary-button {
  flex: 1;
  min-width: 200px;
}

/* Responsive design for review step */
@media (max-width: 768px) {
  .image-review {
    flex-direction: column;
    text-align: center;
  }
  
  .review-artwork {
    width: 120px;
    height: 120px;
  }
  
  .review-actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .review-actions .primary-button,
  .review-actions .secondary-button {
    width: 100%;
    min-width: auto;
  }
  
  .field-comparison {
    gap: 0.75rem;
  }
  
  .auto-populated-value {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .auto-populated-value .label {
    min-width: auto;
    font-size: 0.75rem;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .progress-step,
  .step-number,
  .progress-divider,
  .primary-button,
  .secondary-button,
  .retry-image-button,
  .link-button {
    transition: none;
  }
  
  .primary-button:hover:not(:disabled) {
    transform: none;
  }
  
  .button-spinner,
  .loading-spinner {
    animation: none;
  }
}
</style>